import {
  Button,
  Card,
  Typography,
  Grid,
  Chip,
  withStyles,
  LinearProgress,
  Link,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { deepPurple } from '@material-ui/core/colors'
import { useRouter } from 'next/router'
import React from 'react'
import StarRoundedIcon from '@material-ui/icons/StarRounded'

import { Deal as IDeal } from '../../../../../types/deal'

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '40px auto',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    boxShadow: 'none',
    display: 'flex',
    borderRadius: '1rem',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'visible',
    padding: '30px 50px',
    '@media (max-width: 420px)': {
      margin: '5px',
      padding: '20px',
    },
  },
  mainSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: '2rem',
    margin: '0 0',
  },
  logoContainer: {
    marginTop: '20px',
    marginBottom: '25px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
    '@media (max-width: 420px)': {
      width: '100%',
      maxWidth: '100%',
      margin: '30px 0 0 0',
      justifyContent: 'flex-start',
    },
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    '@media (max-width: 420px)': {
      justifyContent: 'center',
      flexBasis: '100%',
    },
  },
  logo: {
    maxWidth: '100%',
    objectFit: 'contain',
    [theme.breakpoints.up('md')]: {},
  },
  description: {
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  leftSection: {
    width: '70%',
    '@media (max-width: 420px)': {
      width: '100%',
    },
  },
  rightSection: {
    width: '30%',
    '@media (max-width: 420px)': {
      width: '100%',
    },
  },
  descriptionSection: {
    marginTop: '30px',
    marginBottom: '30px',
    paddingRight: '30px',
    '@media (max-width: 420px)': {
      padding: '0',
      margin: '20px auto',
      textAlign: 'center',
    },
  },
  infoSection: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: '25px',
    alignItems: 'center',
  },
  button: {
    marginTop: '20px',
    width: '204px',
    height: '64px',
    fontSize: '18px',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  mobileButton: {
    marginTop: '20px',
    width: '204px',
    height: '64px',
    fontSize: '18px',
    display: 'none',
    '@media (max-width: 420px)': {
      margin: '20px auto',
      display: 'block',
    },
  },
  campaign: {
    color: theme.palette.primary.main,
    width: 'fit-content',
    position: 'relative',
    '& h5': {
      position: 'relative',
    },
    marginBottom: '20px',
    '& > div': {
      marginLeft: '10px',
      marginBottom: '10px',
    },
  },
  campaignTitle: {
    fontSize: '18px',
  },
  campaignBackground: {
    position: 'absolute',
    top: '5px',
    left: '-5px',
    width: '110%',
    backgroundColor: theme.palette.secondary.main,
    height: '26px',
  },
  rating: {
    position: 'relative',
    fontSize: '.8rem',
    '@media (max-width: 420px)': {
      fontSize: '.8rem',
    },
  },
  ratingContainer: {
    width: '100%',
    '@media (max-width: 420px)': {
      margin: '20px auto',
      alignItems: 'baseline',
    },
  },
  ratingBackground: {
    position: 'absolute',
    top: '-10px',
    left: '10px',
    width: '150px',
    backgroundColor: theme.palette.secondary.main,
    height: '61px',
    '@media (max-width: 420px)': {
      width: '120px',
      height: '40px',
      top: '2px',
    },
  },
  hideMobile: {
    '@media (max-width: 420px)': {
      display: 'none',
    },
  },
  promotionSection: {
    position: 'relative',
    width: 'fit-content',
    margin: '20px 0',
  },
  promotion: {
    backgroundColor: deepPurple['400'],
    color: 'white',
    fontWeight: 'bold',
    position: 'relative',
    '@media (max-width: 420px)': {
      fontSize: '14px',
    },
  },
  promotionBackground: {
    position: 'absolute',
    top: '10px',
    left: '-5px',
    width: '90%',
    backgroundColor: theme.palette.secondary.main,
    height: '18px',
    '@media (max-width: 420px)': {
      top: '8px',
      height: '14px',
    },
  },
  feesContainer: {
    margin: '1rem 0',
    '& > p': {
      fontFamily: theme.typography.h1.fontFamily,
    },
    '& > p > span': {
      fontWeight: 'bold',
    },
  },
  actionSection: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '1rem 0',
    '& button': {
      margin: '0 1rem',
      textTransform: 'none',
      borderRadius: '1rem',
    },
    '& a': {
      fontSize: '.8rem',
      borderBottom: '1px solid black',
    },
  },
  markdown: {
    margin: '0',
    fontSize: '18px',
    color: '#555555',
    lineHeight: '1.8em',
    overflowWrap: 'break-word',
    '& img': {
      maxWidth: '100%',
    },
    '& a': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
    '& p': {
      margin: '0',
    },
  },
  detailBody: {},
}))

interface Props {
  deal: IDeal
}

function WalletCard({ deal }: Props): JSX.Element {
  const classes = useStyles({})
  const { push } = useRouter()

  const goToReview = () => push(deal.url)
  return (
    <Card className={classes.root}>
      <div className={classes.mainSection}>
        <div className={classes.logoContainer}>
          <Grid container spacing={2}>
            <Grid container justify="center" item xs={12} md={3}>
              <img
                width={320}
                height={320}
                src={`${deal.image && deal.image.url}`}
                className={classes.logo}
                alt={`${deal.image.name} logo`}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <div className={classes.campaign}>
                {deal.campaign && (
                  <Chip label={deal.campaign.title} color="primary" icon={<StarRoundedIcon />} />
                )}

                {deal.tags.map((tag) => (
                  <Chip key={tag.code} label={tag.name} color="default" />
                ))}
              </div>

              <div className={classes.titleContainer}>
                <Typography variant="h3" className={classes.title}>
                  {deal.title}
                </Typography>
              </div>
              <Typography variant="body1" className={classes.detailBody}>
                {deal.description}
              </Typography>

              {deal.how_to_get && (
                <div className={classes.descriptionSection}>
                  <Typography variant="subtitle1">HOW TO GET</Typography>
                  <Typography color="textSecondary" className={classes.description}>
                    {deal.how_to_get}
                    {/* <ReactMarkdown className={classes.markdown}>{deal.how_to_get}</ReactMarkdown> */}
                  </Typography>
                </div>
              )}

              {deal.restrictions && (
                <div className={classes.descriptionSection}>
                  <Typography variant="subtitle1">RESTRICTIONS</Typography>
                  <Typography color="textSecondary" className={classes.description}>
                    {deal.restrictions}
                    {/* <ReactMarkdown className={classes.markdown}>{deal.how_to_get}</ReactMarkdown> */}
                  </Typography>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
        <div className={classes.infoSection}>
          <div className={classes.actionSection}>
            {/* <Button variant="contained" color="default" onClick={goToReview} disableElevation>
              Compare
            </Button> */}
            <div>
              {/* <Link underline="none" href={card.url} color="inherit">
                Learn more about {card.name}
              </Link> */}
              <Button variant="contained" color="primary" onClick={goToReview} disableElevation>
                Get deal now!
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}

export default WalletCard
